
import React, { useState } from 'react';
import Layout from '@/components/Layout';
import { useUser } from '@/context/UserContext';
import { Card } from '@/components/ui/card';
import { FadeIn } from '@/components/Animations';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserCircle, Bell, Shield, Heart, Award } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import HealthAssessmentTab from '@/components/profile/HealthAssessmentTab';
import ProfileSidebar from '@/components/profile/ProfileSidebar';
import AccountTab from '@/components/profile/tabs/AccountTab';
import BadgesTab from '@/components/profile/tabs/BadgesTab';
import ComingSoonTab from '@/components/profile/tabs/ComingSoonTab';

const Profile = () => {
  const { user, userProgress, logout, isLoading } = useUser();
  const [activeTab, setActiveTab] = useState('account');
  const isMobile = useIsMobile();
  
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center animate-pulse">Loading...</div>
        </div>
      </Layout>
    );
  }
  
  if (!user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold mb-4">Please log in to view your profile</h2>
          <p className="text-muted-foreground">You need to be logged in to access this page.</p>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container max-w-4xl py-8">
        <FadeIn>
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Left sidebar with user information */}
            <ProfileSidebar 
              user={user} 
              userProgress={userProgress} 
              logout={logout}
              setActiveTab={setActiveTab}
            />
            
            <div className="w-full md:w-2/3">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="account" className="flex items-center justify-center gap-1">
                    <UserCircle size={isMobile ? 18 : 16} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Account"}
                  </TabsTrigger>
                  <TabsTrigger value="health" className="flex items-center justify-center gap-1">
                    <Heart size={isMobile ? 18 : 16} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Health Assessment"}
                  </TabsTrigger>
                  <TabsTrigger value="badges" className="flex items-center justify-center gap-1">
                    <Award size={isMobile ? 18 : 16} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Badges"}
                  </TabsTrigger>
                  <TabsTrigger value="notifications" className="flex items-center justify-center gap-1">
                    <Bell size={isMobile ? 18 : 16} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Notifications"}
                  </TabsTrigger>
                  <TabsTrigger value="privacy" className="flex items-center justify-center gap-1">
                    <Shield size={isMobile ? 18 : 16} className={isMobile ? "" : "mr-1"} />
                    {!isMobile && "Privacy"}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="account">
                  <AccountTab user={user} userProgress={userProgress} />
                </TabsContent>
                
                <TabsContent value="health">
                  <HealthAssessmentTab />
                </TabsContent>
                
                <TabsContent value="badges">
                  <BadgesTab userProgress={userProgress} />
                </TabsContent>
                
                <TabsContent value="notifications">
                  <ComingSoonTab 
                    title="Notification Preferences" 
                    description="Control how and when you receive notifications" 
                    icon={Bell} 
                  />
                </TabsContent>
                
                <TabsContent value="privacy">
                  <ComingSoonTab 
                    title="Privacy Settings" 
                    description="Manage your data and privacy preferences" 
                    icon={Shield} 
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </FadeIn>
      </div>
    </Layout>
  );
};

export default Profile;
